/* eslint-disable prefer-destructuring */
/* eslint-disable no-param-reassign */
import {
  Flex,
  Button,
  Text,
  CloseButton,
  Switch,
  Box,
  Badge,
  Tooltip
} from '@chakra-ui/react'
import React, { ReactNode, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { FiEdit2, FiEye, FiEyeOff, FiMenu } from 'react-icons/fi'
import { useDnd } from '../../hooks/useDnd'

interface GroupContainerProps {
  title: string | undefined
  children: ReactNode
  isEditable: boolean
  onAction?: () => void
  onExclude?: () => void
  index: number
  isVisible: boolean
}

interface DropAndDragItem {
  index: number
}

export const GroupContainer = ({
  title,
  children,
  isEditable,
  onExclude,
  onAction,
  isVisible,
  index
}: GroupContainerProps) => {
  const { MoveFormGroup, setListForms, listForms } = useDnd()
  const ref = useRef<any>(null)

  const [{ isDragging }, dragRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    type: 'CONTAINER',
    item: { type: 'CONTAINER', index }
  })

  const [, dropRef] = useDrop({
    accept: 'CONTAINER',
    hover(item: DropAndDragItem, monitor) {
      const draggedIndex = item.index
      const targetIndex = index

      if (draggedIndex === targetIndex) {
        return
      }

      const targetSize = ref?.current.getBoundingClientRect()
      const targetCenter = (targetSize.bottom - targetSize.top) / 2

      const draggedOffset = monitor.getClientOffset()
      const coordY = draggedOffset !== undefined ? draggedOffset?.y : 1
      const coordX = draggedOffset !== undefined ? draggedOffset?.x : 1
      const draggedTop = coordY !== undefined ? coordY - targetSize.top : 1

      if (draggedIndex < targetIndex && draggedTop < targetCenter) {
        return
      }

      if (draggedIndex > targetIndex && draggedTop > targetCenter) {
        return
      }

      MoveFormGroup(0, draggedIndex, targetIndex)

      item.index = targetIndex
    }
  })

  const handlerVisibility = (event: React.ChangeEvent<HTMLInputElement>) => {
    const values = [...listForms]
    const valuesAux = values.slice(index, index + 1)
    const newValue = valuesAux.map((group) => {
      return {
        id: group.id,
        isVisible: event.target.checked,
        fields: group.fields,
        name: group.name
      }
    })

    values[index] = newValue[0]
    setListForms(values)
  }

  dragRef(dropRef(ref))

  const hasError = title === ''

  return (
    <Box
      ref={ref}
      w="100%"
      bg="white"
      borderRadius={10}
      boxShadow={
        isDragging
          ? 'none'
          : '0px 2px 8px rgba(0, 0, 0, 0.06), 0px 0px 0px 1px rgba(0,0,0,0.06)'
      }
      border={hasError ? '1.5px solid #E53E3E' : '1.5px solid #E2E8F0'}
      opacity={isDragging ? 0.4 : 1}
      overflow="hidden"
      transition="box-shadow 0.2s"
    >
      {/* Group header */}
      <Flex
        px="4"
        py="3"
        alignItems="center"
        gap={3}
        bg={hasError ? 'red.50' : 'gray.50'}
        borderBottom="1px solid #E2E8F0"
      >
        {/* Drag handle */}
        <Box color="gray.400" cursor="grab" flexShrink={0}>
          <FiMenu size={16} />
        </Box>

        {/* Title */}
        <Text
          fontWeight="semibold"
          fontSize="sm"
          color={hasError ? 'red.500' : 'gray.700'}
          flex={1}
          noOfLines={1}
        >
          {hasError ? 'Nome do grupo obrigatório' : title}
        </Text>

        {isEditable && (
          <Flex alignItems="center" gap={3}>
            {/* Visibility toggle */}
            <Flex alignItems="center" gap={2}>
              <Box color={isVisible ? 'green.500' : 'gray.400'} fontSize="sm">
                {isVisible ? <FiEye size={14} /> : <FiEyeOff size={14} />}
              </Box>
              <Tooltip
                label={isVisible ? 'Ocultar campos' : 'Mostrar campos'}
                placement="top"
                hasArrow
                fontSize="xs"
              >
                <Switch
                  size="sm"
                  isChecked={listForms[index].isVisible}
                  colorScheme="green"
                  onChange={(event) => handlerVisibility(event)}
                />
              </Tooltip>
            </Flex>

            {/* Edit button */}
            <Tooltip label="Editar grupo" placement="top" hasArrow fontSize="xs">
              <Button
                type="button"
                colorScheme="blue"
                variant="ghost"
                size="xs"
                px={2}
                onClick={() => onAction?.()}
              >
                <FiEdit2 size={14} />
              </Button>
            </Tooltip>

            {/* Delete button */}
            <Tooltip label="Excluir grupo" placement="top" hasArrow fontSize="xs">
              <CloseButton
                size="sm"
                bg="red.500"
                color="white"
                borderRadius="md"
                _hover={{ bg: 'red.600' }}
                onClick={() => onExclude?.()}
              />
            </Tooltip>
          </Flex>
        )}
      </Flex>

      {/* Group fields content */}
      {isVisible && (
        <Box px="4" py="4">
          {children}
        </Box>
      )}

      {/* Hidden state indicator */}
      {!isVisible && (
        <Flex
          px="4"
          py="2"
          alignItems="center"
          gap={2}
          bg="gray.50"
          borderTop="1px solid #E2E8F0"
        >
          <FiEyeOff size={12} color="#A0AEC0" />
          <Text fontSize="xs" color="gray.400">
            Campos ocultos no formulário
          </Text>
        </Flex>
      )}
    </Box>
  )
}
